// src/components/RecallSuccessOverlay.js
import React, { useState, useEffect } from 'react';

export default function RecallSuccessOverlay({ onComplete }) {
  const [isVisible, setIsVisible] = useState(false);
  const [checkedItems, setCheckedItems] = useState([]);
  const [showButton, setShowButton] = useState(false);

  const steps = [
    { id: 'personalized', label: 'Personalized', icon: '✏️' },
    { id: 'sent', label: 'Sent', icon: '📤' },
    { id: 'replied', label: 'Patient Replied', icon: '💬' },
    { id: 'notified', label: 'Reception Notified', icon: '🏥' },
  ];

  // Fade-in animation
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Animate checklist items one by one
  useEffect(() => {
    if (!isVisible) return;

    let currentIndex = 0;
    const allChecked = [];

    const checkNext = () => {
      if (currentIndex >= steps.length) {
        // Show button after all items are checked
        setTimeout(() => setShowButton(true), 600);
        return;
      }

      allChecked.push(steps[currentIndex].id);
      setCheckedItems([...allChecked]);
      currentIndex++;

      setTimeout(checkNext, 500);
    };

    // Start checklist animation after a brief delay
    const timer = setTimeout(checkNext, 800);
    return () => clearTimeout(timer);
  }, [isVisible]);

  // Auto-transition after button appears
  useEffect(() => {
    if (showButton) {
      const timer = setTimeout(() => {
        if (onComplete) onComplete();
      }, 5000); // Auto-transition after 5 seconds
      
      return () => clearTimeout(timer);
    }
  }, [showButton, onComplete]);

  const handleComplete = () => {
    if (onComplete) onComplete();
  };

  return (
    <div style={successStyles.overlay}>
      <div style={successStyles.glowBg} />
      
      <div 
        style={{
          ...successStyles.content,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
          transition: 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {/* Checkmark Circle */}
        <div style={successStyles.checkmarkContainer}>
          <div style={successStyles.checkmarkCircle}>
            <svg 
              style={successStyles.checkmarkSvg} 
              viewBox="0 0 52 52" 
              fill="none"
            >
              <circle 
                cx="26" 
                cy="26" 
                r="25" 
                stroke="#3ABFA8" 
                strokeWidth="2"
                style={successStyles.checkmarkCircleSvg}
              />
              <path 
                d="M14 27L22 35L38 19" 
                stroke="#3ABFA8" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={successStyles.checkmarkPath}
              />
            </svg>
          </div>
        </div>

        {/* Headline */}
        <h1 style={successStyles.headline}>
          Patient Successfully Recalled
        </h1>
        <p style={successStyles.subheadline}>
          The recall process is complete
        </p>

        {/* Checklist */}
        <div style={successStyles.checklistContainer}>
          {steps.map((step) => {
            const isChecked = checkedItems.includes(step.id);
            return (
              <div 
                key={step.id}
                style={{
                  ...successStyles.checklistItem,
                  opacity: isChecked ? 1 : 0.2,
                  transform: isChecked ? 'translateX(0)' : 'translateX(-10px)',
                  transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              >
                <span style={successStyles.checklistIcon}>
                  {isChecked ? '✓' : step.icon}
                </span>
                <span style={{
                  ...successStyles.checklistLabel,
                  color: isChecked ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
                }}>
                  {step.label}
                </span>
                {isChecked && (
                  <span style={successStyles.checklistCheck}>✓</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Final Message */}
        <div style={successStyles.finalMessage}>
          <span style={successStyles.finalMessageIcon}>🎯</span>
          <span style={successStyles.finalMessageText}>
            Your front desk only handles patients who are ready to schedule.
          </span>
        </div>

        {/* Transition Button */}
        {showButton && (
          <button 
            onClick={handleComplete}
            style={successStyles.transitionButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(45, 155, 138, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(45, 155, 138, 0.2)';
            }}
          >
            <span style={successStyles.buttonText}>Enter Dashboard</span>
            <span style={successStyles.buttonArrow}>→</span>
          </button>
        )}

        {/* Subtle footer */}
        <div style={successStyles.footer}>
          <span style={successStyles.footerText}>
            {showButton ? 'Ready to continue' : 'Completing process...'}
          </span>
        </div>
      </div>
    </div>
  );
}

const successStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: '#070E16',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', -apple-system, sans-serif",
    padding: 24,
    overflow: 'hidden',
  },
  glowBg: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    maxWidth: 700,
    height: '60%',
    maxHeight: 600,
    background: 'radial-gradient(circle, rgba(45, 155, 138, 0.06) 0%, rgba(45, 155, 138, 0.02) 40%, rgba(45, 155, 138, 0) 70%)',
    filter: 'blur(60px)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  content: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 560,
    width: '100%',
    gap: 24,
  },

  // Checkmark
  checkmarkContainer: {
    marginBottom: 8,
  },
  checkmarkCircle: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    background: 'rgba(45, 155, 138, 0.08)',
    border: '2px solid rgba(45, 155, 138, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  checkmarkSvg: {
    width: 52,
    height: 52,
  },
  checkmarkCircleSvg: {
    animation: 'rotate 0.6s ease-out',
  },
  checkmarkPath: {
    strokeDasharray: 50,
    strokeDashoffset: 50,
    animation: 'drawCheckmark 0.6s ease-out 0.3s forwards',
  },

  // Headline
  headline: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: 700,
    margin: 0,
    letterSpacing: '-0.5px',
    textAlign: 'center',
  },
  subheadline: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 15,
    fontWeight: 400,
    margin: '-8px 0 0 0',
    textAlign: 'center',
    letterSpacing: '0.3px',
  },

  // Checklist
  checklistContainer: {
    width: '100%',
    maxWidth: 420,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '4px 0',
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 16px',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.04)',
    transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
  },
  checklistIcon: {
    fontSize: 16,
    width: 24,
    textAlign: 'center',
  },
  checklistLabel: {
    fontSize: 14,
    fontWeight: 500,
    flex: 1,
    transition: 'color 0.3s ease',
  },
  checklistCheck: {
    color: '#3ABFA8',
    fontSize: 14,
    fontWeight: 700,
    animation: 'fadeIn 0.3s ease-out',
  },

  // Final Message
  finalMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 20px',
    background: 'rgba(45, 155, 138, 0.06)',
    border: '1px solid rgba(45, 155, 138, 0.12)',
    borderRadius: 12,
    width: '100%',
    maxWidth: 420,
    marginTop: 4,
  },
  finalMessageIcon: {
    fontSize: 18,
  },
  finalMessageText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.2px',
  },

  // Button
  transitionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 32px',
    background: 'linear-gradient(135deg, #2D9B8A 0%, #238A7A 100%)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 30,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    boxShadow: '0 4px 16px rgba(45, 155, 138, 0.2)',
    transition: 'all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    animation: 'fadeIn 0.6s ease-out',
    letterSpacing: '0.3px',
  },
  buttonText: {
    color: '#FFFFFF',
  },
  buttonArrow: {
    fontSize: 18,
    transition: 'transform 0.3s ease',
  },

  // Footer
  footer: {
    marginTop: 4,
  },
  footerText: {
    color: 'rgba(255,255,255,0.15)',
    fontSize: 12,
    letterSpacing: '0.3px',
  },
};

// Add keyframes
const successStyleSheet = document.createElement("style");
successStyleSheet.textContent = `
  @keyframes drawCheckmark {
    0% { stroke-dashoffset: 50; opacity: 0; }
    100% { stroke-dashoffset: 0; opacity: 1; }
  }
  @keyframes rotate {
    0% { transform: rotate(-90deg); opacity: 0; }
    100% { transform: rotate(0deg); opacity: 1; }
  }
  @keyframes fadeIn {
    0% { opacity: 0; transform: scale(0.8); }
    100% { opacity: 1; transform: scale(1); }
  }
`;
document.head.appendChild(successStyleSheet);